// src/services/cloudRunServices.js
import { getApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import { getFirebaseAppCheckToken } from "./firebaseAppCheckServices";
import { runWithClientDeadline } from "./clientRequestDeadline";
import { createApiRequestId, buildApiRequestHeaders, normalizeRequestHeaders } from "./apiRequestIdentity";

const isLocalhost =
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1";

const LOCAL_API_BASE_URL =
  process.env.REACT_APP_LOCAL_API_BASE_URL || "http://localhost:8088";
const AUTH_HANDLER_DIRECT_BASE_URL =
  process.env.REACT_APP_AUTH_HANDLER_DIRECT_BASE_URL ||
  "https://auth-handler-dgoxloodfq-uc.a.run.app";

const MEMBER_STRIPE_CHECKOUT_HEALTH = Object.freeze({
  healthWorkflow: "member_stripe_checkout_runtime",
  healthMinDegradedCount: 1,
  healthCriticalCount: 1,
});

const ACCOUNT_ACCESS_RUNTIME_HEALTH = Object.freeze({
  healthWorkflow: "account_access_runtime",
  healthExpectedErrorCodes: ["rate_limited", "too_many_attempts", "resend_cooldown", "auth/too-many-requests"],
  healthMinDegradedCount: 1,
  healthCriticalCount: 1,
  healthNetworkMinDegradedCount: 1,
  healthNetworkCriticalCount: 1,
});

const MEMBERSHIP_PAYMENT_HEALTH = Object.freeze({
  healthWorkflow: "member_membership_offer_checkout_payment",
  healthMinDegradedCount: 1,
  healthCriticalCount: 1,
});

const MEMBERSHIP_OFFER_HEALTH = Object.freeze({
  ...MEMBERSHIP_PAYMENT_HEALTH,
  healthNetworkMinDegradedCount: 3,
  healthNetworkCriticalCount: 8,
});

const VISIT_CHECK_IN_HEALTH = Object.freeze({
  healthWorkflow: "helper_visit_check_in",
  healthMinDegradedCount: 1,
  healthCriticalCount: 8,
});

const getAuthClient = () => getAuth(getApp());

export const resolveCloudRunEndpointUrl = ({
  local,
  localBaseUrl,
  apiPath,
  directBaseUrl,
  route,
}) => {
  if (local) return `${localBaseUrl}${apiPath}`;
  const directBase = String(directBaseUrl || "").replace(/\/+$/, "");
  return directBase ? `${directBase}/${route}` : apiPath;
};

const getLocalDevAuthHeaders = async (user) => {
  if (!isLocalhost || !user) return {};
  const tokenResult = await user.getIdTokenResult().catch(() => null);
  const claims = tokenResult?.claims || {};
  const role =
    claims?.role === "admin" || claims?.isAdmin
      ? "admin"
      : claims?.role === "employee" ||
          claims?.role === "helper" ||
          claims?.isEmployee ||
          claims?.isHelper
        ? "employee"
        : claims?.role === "member" || claims?.isMember
          ? "member"
          : "";
  return {
    "x-dev-uid": user.uid,
    ...(role ? { "x-dev-role": role } : {}),
  };
};

// Service + endpoint map (client only needs paths + routes now)
const serviceEndpointMap = {
  "stripe-handler": {
    localPrefix: "/stripe",
    endpoints: {
      getStripeLoginLink: {
        route: "get-stripe-login-link",
        requiresAuth: true,
      },
      getStripeOnboardingLink: {
        route: "get-stripe-onboarding-link",
        requiresAuth: true,
      },
      refreshStripeRepairState: {
        route: "refresh-stripe-repair-state",
        requiresAuth: true,
      },
      addCardToCustomer: {
        route: "add-card-to-customer",
        requiresAuth: true,
        ...MEMBER_STRIPE_CHECKOUT_HEALTH,
      },
      createPaymentIntent: {
        route: "create-payment-intent",
        requiresAuth: true,
        ...MEMBERSHIP_PAYMENT_HEALTH,
      },
      prepareDeferredMembership: {
        route: "prepare-deferred-membership",
        requiresAuth: true,
        ...MEMBERSHIP_PAYMENT_HEALTH,
      },
      clearPreparedDeferredMembership: {
        route: "clear-prepared-deferred-membership",
        requiresAuth: true,
        ...MEMBERSHIP_PAYMENT_HEALTH,
      },
      changeDeferredMembershipContribution: {
        route: "change-deferred-membership-contribution",
        requiresAuth: true,
        ...MEMBERSHIP_PAYMENT_HEALTH,
      },
      cancelPendingDeferredMembership: {
        route: "cancel-pending-deferred-membership",
        requiresAuth: true,
        ...MEMBERSHIP_PAYMENT_HEALTH,
      },
      recoverDeferredMembership: {
        route: "recover-deferred-membership",
        requiresAuth: true,
        ...MEMBERSHIP_PAYMENT_HEALTH,
      },
      createMembershipTaxQuote: {
        route: "create-membership-tax-quote",
        requiresAuth: true,
        ...MEMBERSHIP_PAYMENT_HEALTH,
      },
      createSetupIntent: {
        route: "create-setup-intent",
        requiresAuth: true,
        requestTimeoutMs: 15_000,
        ...MEMBER_STRIPE_CHECKOUT_HEALTH,
      },
      createStripeCustomer: {
        route: "create-stripe-customer",
        requiresAuth: true,
        ...MEMBER_STRIPE_CHECKOUT_HEALTH,
      },
      fetchCards: {
        route: "fetch-cards",
        requiresAuth: true,
        requestTimeoutMs: 25_000,
        retry: { retries: 1, retryOnStatuses: [429, 500, 502, 503, 504] },
        reportRetryFailureImmediately: false,
        ...MEMBER_STRIPE_CHECKOUT_HEALTH,
      },
      getVisitInvoiceHostedUrl: {
        route: "get-visit-invoice-hosted-url",
        requiresAuth: true,
        requestTimeoutMs: 12_000,
      },
      processVisitRefund: {
        route: "process-visit-refund",
        requiresAuth: true,
      },
      preauthorizeCard: { route: "preauthorize-card", requiresAuth: true },
      removePaymentMethod: {
        route: "remove-payment-method",
        requiresAuth: true,
        ...MEMBER_STRIPE_CHECKOUT_HEALTH,
      },
      dismissMembershipChangeNotice: { route: "dismiss-membership-change-notice", requiresAuth: true },
      refundMembership: { route: "refund-membership", requiresAuth: true },
      updateDefaultPaymentMethod: {
        route: "update-default-payment-method",
        requiresAuth: true,
        ...MEMBER_STRIPE_CHECKOUT_HEALTH,
      },
      updateConnectedAccountEmail: {
        route: "update-connected-account-email",
        requiresAuth: true,
      },
      updateStripeUser: {
        route: "update-stripe-user",
        requiresAuth: true,
      },
    },
  },

  "earnings-handler": {
    localPrefix: "/earnings",
    endpoints: {
      claimLevel: {
        route: "helper/claim-level",
        requiresAuth: true,
      },
      claimLevelRaise: {
        route: "helper/claim-level-raise",
        requiresAuth: true,
      },
      getEarningsOverview: {
        route: "helper/get-earnings-overview",
        requiresAuth: true,
      },
      requestPayout: {
        route: "helper/request-payout",
        requiresAuth: true,
      },
      requestReservePayout: {
        route: "helper/request-reserve-payout",
        requiresAuth: true,
      },
      getHelperFinancialState: {
        route: "admin/get-helper-financial-state",
        requiresAuth: true,
      },
    },
  },

  "member-onboarding-handler": {
    localPrefix: "/member-onboarding",
    endpoints: {
      puzzleBoard: { route: "puzzleBoard", requiresAuth: false, requestTimeoutMs: 15000 },
      getMemberStampCard: { route: "getMemberStampCard", requiresAuth: true },
      claimMemberStamp: { route: "claimMemberStamp", requiresAuth: true },
      acknowledgeMemberStampNotification: { route: "acknowledgeMemberStampNotification", requiresAuth: true },
      checkPhoneAvailable: {
        route: "checkPhoneAvailable",
        requiresAuth: false,
        includeAuthToken: false,
      },
      resolvePublicBookingAccess: {
        route: "resolvePublicBookingAccess",
        requiresAuth: true,
        healthWorkflow: "pre_onboarding_v2_runtime",
      },
      createMemberAccount: {
        route: "createMemberAccount",
        requiresAuth: true,
      },
      ensureMemberCoreDocs: {
        route: "ensureMemberCoreDocs",
        requiresAuth: true,
      },
      populateMemberDocs: {
        route: "populateMemberDocs",
        requiresAuth: true,
        ...MEMBER_STRIPE_CHECKOUT_HEALTH,
      },
      acceptMemberAgreement: {
        route: "acceptMemberAgreement",
        requiresAuth: true,
      },
      pricingSetup: { route: "pricingSetup", requiresAuth: true },
      resolveMembershipPricingOffer: {
        route: "resolveMembershipPricingOffer",
        requiresAuth: true,
      },
      resolvePublicMembershipPricingOffer: {
        route: "resolvePublicMembershipPricingOffer",
        requiresAuth: false,
        includeAppCheckToken: true,
        // Reuse the same offer/session after a temporary edge failure. Only
        // an exhausted lookup is a terminal membership dependency failure.
        retry: { retries: 1, retryOnStatuses: [502, 503, 504] },
        reportRetryFailureImmediately: false,
        ...MEMBERSHIP_OFFER_HEALTH,
      },
      acknowledgePublicMembershipPricingExposure: {
        route: "acknowledgePublicMembershipPricingExposure",
        requiresAuth: false,
        includeAppCheckToken: true,
      },
      updatePricing: { route: "updatePricing", requiresAuth: true },
      markSignupFeeComplete: {
        route: "markSignupFeeComplete",
        requiresAuth: true,
        ...MEMBERSHIP_PAYMENT_HEALTH,
      },
      recordMembershipContributionDecision: {
        route: "recordMembershipContributionDecision",
        requiresAuth: true,
        ...MEMBERSHIP_PAYMENT_HEALTH,
      },
      markOnboardingComplete: {
        route: "markOnboardingComplete",
        requiresAuth: true,
      },
      activateDashboardOnboarding: {
        route: "activateDashboardOnboarding",
        requiresAuth: true,
      },
      applyMembershipRetentionDiscount: {
        route: "applyMembershipRetentionDiscount",
        requiresAuth: true,
      },
    },
  },

  "meta-handler": {
    localPrefix: "/meta",
    endpoints: {
      track: { route: "track", requiresAuth: false },
    },
  },

  "funnel-telemetry-handler": {
    localPrefix: "/funnel-telemetry",
    endpoints: {
      trackFunnelTelemetry: {
        route: "track",
        requiresAuth: false,
        includeAppCheckToken: true,
      },
      trackExperimentTelemetry: {
        route: "experiment/track",
        requiresAuth: false,
        includeAuthToken: true,
        includeAppCheckToken: true,
      },
      getScheduleVisitExitReasons: {
        route: "schedule-visit/exit-reasons",
        requiresAuth: true,
        includeAppCheckToken: true,
      },
    },
  },

  "meta-ads-handler": {
    localPrefix: "/meta-ads",
    endpoints: {
      buildTieredHelperCircles: {
        route: "geoRetargeting/buildTieredHelperCircles",
        requiresAuth: true,
      },
      previewTieredHelperCircles: {
        route: "geoRetargeting/previewTieredHelperCircles",
        requiresAuth: true,
        localDevAdmin: true,
      },
      listCustomAudiences: {
        route: "geoRetargeting/listCustomAudiences",
        requiresAuth: true,
      },
      getAdSetAudienceIds: {
        route: "geoRetargeting/getAdSetAudienceIds",
        requiresAuth: true,
      },
      applyTieredRetargetingToMeta: {
        route: "geoRetargeting/applyTieredRetargetingToMeta",
        requiresAuth: true,
      },
      syncGoogleGeoTargeting: {
        route: "googleAds/syncGeoTargeting",
        requiresAuth: true,
      },
      uploadGoogleOfflineConversions: {
        route: "googleAds/uploadOfflineConversions",
        requiresAuth: true,
      },
      syncGoogleOfflineConversions: {
        route: "googleAds/syncOfflineConversions",
        requiresAuth: true,
      },
      validateGoogleAdsSetup: {
        route: "googleAds/validateSetup",
        requiresAuth: true,
      },
    },
  },

  "twilio-handler": {
    localPrefix: "/twilio",
    endpoints: {
      resolvePublicLocalSupportSmsNumber: {
        route: "public/local-support-sms-number",
        requiresAuth: false,
        includeAuthToken: false,
        healthWorkflow: "landing_local_sms_number_resolution",
      },
      updateSupportProxy: { route: "updateSupportProxy", requiresAuth: true },
    },
  },

  "helper-handler": {
    localPrefix: "/helper",
    endpoints: {
      getUpcomingOrientationEvents: {
        route: "getUpcomingOrientationEvents",
        requiresAuth: true,
      },
      attendOrientation: { route: "attendOrientation", requiresAuth: true },
      removeOrientation: { route: "removeOrientation", requiresAuth: true },
      registerInAppOrientation: {
        route: "inAppOrientation/register",
        requiresAuth: true,
      },
      cancelInAppOrientation: {
        route: "inAppOrientation/cancel",
        requiresAuth: true,
      },
      recordInAppOrientationHeartbeat: {
        route: "inAppOrientation/heartbeat",
        requiresAuth: true,
      },
      // IN_APP_ORIENTATION_OBSERVABILITY_PRUNE_TAG: launch diagnostics endpoint mapping.
      recordInAppOrientationClientEvent: {
        route: "inAppOrientation/clientEvent",
        requiresAuth: true,
      },
      sendInAppOrientationMessage: {
        route: "inAppOrientation/sendMessage",
        requiresAuth: true,
      },
      sendInAppOrientationSupportMessage: {
        route: "inAppOrientation/sendSupportMessage",
        requiresAuth: true,
      },
      setInAppOrientationSupportPresence: {
        route: "inAppOrientation/setSupportPresence",
        requiresAuth: true,
      },
      setHelperSuspension: {
        route: "setHelperSuspension",
        requiresAuth: true,
      },
      setHelperActiveState: {
        route: "setHelperActiveState",
        requiresAuth: true,
      },
      markHelperApplicationMilestone: {
        route: "markHelperApplicationMilestone",
        requiresAuth: true,
      },
      getActiveHelperAgreementBundle: {
        route: "legalAgreements/active",
        requiresAuth: true,
      },
      acceptHelperAgreementBundle: {
        route: "legalAgreements/accept",
        requiresAuth: true,
      },
      getParentAuthorizationStatus: {
        route: "legalAgreements/parent/status",
        requiresAuth: true,
      },
      renewParentAuthorization: {
        route: "legalAgreements/parent/renew",
        requiresAuth: true,
      },
      resendParentAuthorization: {
        route: "legalAgreements/parent/resend",
        requiresAuth: true,
      },
      resolveParentAuthorization: {
        route: "legalAgreements/parent/resolve",
        requiresAuth: false,
        includeAuthToken: false,
      },
      acceptParentAuthorization: {
        route: "legalAgreements/parent/accept",
        requiresAuth: false,
        includeAuthToken: false,
      },
      completeHelperOnboarding: {
        route: "completeHelperOnboarding",
        requiresAuth: true,
      },
      markHelperOnboardingPending: {
        route: "markHelperOnboardingPending",
        requiresAuth: true,
      },
      submitFlyerPost: {
        route: "flyers/submitPost",
        requiresAuth: true,
      },
      assessFlyerScene: {
        route: "flyers/assessScene",
        requiresAuth: true,
      },
      sendFlyerInstructions: {
        route: "flyers/sendInstructions",
        requiresAuth: true,
      },
      transitionHelperHiringStage: {
        route: "admin/transitionHelperHiringStage",
        requiresAuth: true,
      },
      setHelperHiringStatus: {
        route: "admin/setHelperHiringStatus",
        requiresAuth: true,
      },
      requestHelperFinalizeRetry: {
        route: "admin/requestHelperFinalizeRetry",
        requiresAuth: true,
      },
      getHelperDiscountState: {
        route: "helperDiscounts/get",
        requiresAuth: true,
      },
      getHelperDiscountPolicy: {
        route: "helperDiscounts/policy",
        requiresAuth: true,
      },
      upsertHelperDiscount: {
        route: "helperDiscounts/upsert",
        requiresAuth: true,
      },
      endHelperDiscount: {
        route: "helperDiscounts/end",
        requiresAuth: true,
      },
      resetHelperDiscountQuota: {
        route: "helperDiscounts/resetQuota",
        requiresAuth: true,
      },
      getHelperRateState: {
        route: "helperRates/get",
        requiresAuth: true,
        healthWorkflow: "helper_rate_canonical_migration",
      },
      setHelperRatePreferences: {
        route: "helperRates/set",
        requiresAuth: true,
        healthWorkflow: "helper_rate_canonical_migration",
      },
      recordHelperRateGuardShown: {
        route: "helperRates/guard-shown",
        requiresAuth: true,
        healthWorkflow: "helper_rate_canonical_migration",
      },
      reviewHiringRecommendationHelper: {
        route: "hiring-recommendations/reviewHelper",
        requiresAuth: true,
      },
      getHiringRecommendationScalarConfig: {
        route: "hiring-recommendations/scalarConfig",
        requiresAuth: true,
      },
      enqueueHiringRecommendationLatestReviews: {
        route: "hiring-recommendations/enqueueLatestReviews",
        requiresAuth: true,
      },
      enqueueHiringPassingRecommendations: {
        route: "hiring-recommendations/enqueuePassingRecommendations",
        requiresAuth: true,
      },
    },
  },

  "visit-decline-handler": {
    localPrefix: "/visit-decline",
    endpoints: {
      handleDeclineVisit: { route: "handleDeclineVisit", requiresAuth: true },
    },
  },

  "visit-actions-handler": {
    localPrefix: "/visit-actions",
    endpoints: {
      createVisit: {
        route: "createVisit",
        requiresAuth: true,
        healthWorkflow: "member_visit_booking_server",
        healthNetworkMinDegradedCount: 3,
        healthNetworkCriticalCount: 8,
      },
      completePublicBookingVisit: {
        route: "completePublicBookingVisit",
        requiresAuth: true,
      },
      recoverPublicBookingAfterPayment: {
        route: "recoverPublicBookingAfterPayment",
        requiresAuth: true,
      },
      markPublicBookingPaymentPrepared: {
        route: "markPublicBookingPaymentPrepared",
        requiresAuth: true,
      },
      markPublicBookingPaymentSucceeded: {
        route: "markPublicBookingPaymentSucceeded",
        requiresAuth: true,
      },
      pickupOpenVisit: { route: "pickupOpenVisit", requiresAuth: true },
      resolveAcceptanceExperiment: { route: "resolveAcceptanceExperiment", requiresAuth: true, requestTimeoutMs: 15_000 },
      recordVisitCommunicationInteraction: { route: "recordVisitCommunicationInteraction", requiresAuth: true, requestTimeoutMs: 15_000 },
      acceptVisit: {
        route: "acceptVisit", requiresAuth: true, requestTimeoutMs: 15_000,
        retry: { retries: 1, retryOnStatuses: [502, 503, 504] },
      },
      cancelVisit: { route: "cancelVisit", requiresAuth: true },
      changeVisitTime: { route: "changeVisitTime", requiresAuth: true },
      markHelperNoShow: { route: "markHelperNoShow", requiresAuth: true },
      reverseHelperNoShow: { route: "reverseHelperNoShow", requiresAuth: true },
      markVisitNonBillable: {
        route: "markVisitNonBillable",
        requiresAuth: true,
      },
      reverseVisitNonBillable: {
        route: "reverseVisitNonBillable",
        requiresAuth: true,
      },
      saveVisitAddresses: { route: "saveVisitAddresses", requiresAuth: true },
      getVisitCheckInState: { route: "getVisitCheckInState", requiresAuth: true, requestTimeoutMs: 15000, ...VISIT_CHECK_IN_HEALTH },
      submitVisitCheckIn: {
        route: "submitVisitCheckIn", requiresAuth: true, requestTimeoutMs: 15000, ...VISIT_CHECK_IN_HEALTH,
        healthExpectedErrorCodes: ["already_submitted", "missing_answer", "visit_unavailable"],
      },
      sendTravelUpdate: { route: "sendTravelUpdate", requiresAuth: true, requestTimeoutMs: 15000 },
    },
  },

  "messages-handler": {
    localPrefix: "/messages",
    endpoints: {
      sendAppSms: { route: "sendAppSms", requiresAuth: true },
      getOutboundSmsAnalytics: {
        route: "admin/outboundSmsAnalytics",
        requiresAuth: true,
      },
      listOutboundSmsSends: {
        route: "admin/outboundSmsSends",
        requiresAuth: true,
      },
    },
  },

  "crash-handler": {
    localPrefix: "/crash",
    endpoints: {
      resolveCrashIssues: {
        route: "admin/resolveIssues",
        requiresAuth: true,
      },
      markCrashIssuesPendingDeploy: {
        route: "admin/markIssuesPendingDeploy",
        requiresAuth: true,
      },
    },
  },

  "product-issues-handler": {
    localPrefix: "/product-issues",
    endpoints: {
      reportProductIssue: {
        route: "report",
        requiresAuth: true,
      },
      listProductIssues: {
        route: "admin/listIssues",
        requiresAuth: true,
      },
      updateProductIssue: {
        route: "admin/updateIssue",
        requiresAuth: true,
      },
      listProductIssuePolicies: {
        route: "admin/listPolicies",
        requiresAuth: true,
      },
      upsertProductIssuePolicy: {
        route: "admin/upsertPolicy",
        requiresAuth: true,
      },
    },
  },

  "communications-handler": {
    localPrefix: "/communications",
    endpoints: {
      getPublicAssistantBundle: {
        route: "getPublicAssistantBundle",
        requiresAuth: false,
      },
      answerPublicAssistant: {
        route: "answerPublicAssistant",
        requiresAuth: false,
      },
      answerBookingAssistant: {
        route: "answerBookingAssistant",
        requiresAuth: true,
      },
      closeBookingAssistant: {
        route: "closeBookingAssistant",
        requiresAuth: true,
      },
      submitPublicInquiry: {
        route: "submitPublicInquiry",
        requiresAuth: false,
      },
      openThread: { route: "openThread", requiresAuth: true },
      sendThreadMessage: { route: "sendThreadMessage", requiresAuth: true },
      createMediaUpload: { route: "createMediaUpload", requiresAuth: true },
      finalizeMediaUpload: { route: "finalizeMediaUpload", requiresAuth: true },
      resolveMediaUrls: { route: "resolveMediaUrls", requiresAuth: true },
      markThreadRead: { route: "markThreadRead", requiresAuth: true },
      markAdminThreadsReadBefore: {
        route: "markAdminThreadsReadBefore",
        requiresAuth: true,
      },
      markThreadViewed: { route: "markThreadViewed", requiresAuth: true },
      markThreadSeen: { route: "markThreadSeen", requiresAuth: true },
      markThreadUnread: { route: "markThreadUnread", requiresAuth: true },
      getAgentSupportWorkspace: {
        route: "getAgentSupportWorkspace",
        requiresAuth: true,
      },
      recordReliabilityEvent: {
        route: "reliabilityEvents/record",
        requiresAuth: true,
      },
      createAgentTuningIssue: {
        route: "agentSupport/createTuningIssue",
        requiresAuth: true,
      },
      updateAgentCapabilityRequest: {
        route: "agentSupport/updateCapabilityRequest",
        requiresAuth: true,
      },
      updateAgentTuningIssue: {
        route: "agentSupport/updateTuningIssue",
        requiresAuth: true,
      },
      getThreadParticipants: {
        route: "getThreadParticipants",
        requiresAuth: true,
      },
      moderateHelperMember: {
        route: "moderateHelperMember",
        requiresAuth: true,
      },
      listThreadEmailThreads: {
        route: "listThreadEmailThreads",
        requiresAuth: true,
      },
      setThreadHelperParticipation: {
        route: "setThreadHelperParticipation",
        requiresAuth: true,
      },
      setMessageHelperParticipation: {
        route: "setMessageHelperParticipation",
        requiresAuth: true,
      },
      reclassifyThreadMessage: {
        route: "reclassifyThreadMessage",
        requiresAuth: true,
      },
      syncVisitThreadHelpers: {
        route: "syncVisitThreadHelpers",
        requiresAuth: true,
      },
      moderateMemberHelper: {
        route: "moderateMemberHelper",
        requiresAuth: true,
      },
    },
  },

  "gmail-handler": {
    localPrefix: "/gmail",
    endpoints: {
      getGmailWatchStatus: { route: "status/gmailWatch", requiresAuth: true },
      sendInboxMessage: { route: "sendInboxMessage", requiresAuth: true },
    },
  },

  "auth-handler": {
    localPrefix: "/auth",
    endpoints: {
      checkLoginAccount: {
        route: "checkLoginAccount",
        requiresAuth: true,
        requestTimeoutMs: 1500,
        healthWorkflow: "account_access_hint_runtime",
        healthExpectedErrorCodes: ["invalid_argument"],
      },
      sendLoginVerificationCode: {
        route: "sendPublicBookingVerificationCode",
        requiresAuth: true,
        requestTimeoutMs: 25_000,
        healthWorkflow: "account_access_runtime",
        healthExpectedErrorCodes: ["rate_limited", "resend_cooldown", "account_not_found", "identity_conflict"],
        healthMinDegradedCount: 1,
        healthCriticalCount: 1,
        healthNetworkMinDegradedCount: 1,
        healthNetworkCriticalCount: 1,
      },
      validateLoginVerificationCode: {
        route: "validatePublicBookingVerificationCode",
        requiresAuth: true,
        requestTimeoutMs: 25_000,
        healthWorkflow: "account_access_runtime",
        healthExpectedErrorCodes: ["invalid_code", "code_expired", "code_used", "rate_limited", "too_many_attempts"],
        healthMinDegradedCount: 1,
        healthCriticalCount: 1,
        healthNetworkMinDegradedCount: 1,
        healthNetworkCriticalCount: 1,
      },
      signInWithAccountPassword: {
        route: "signInWithAccountPassword",
        requiresAuth: true,
        requestTimeoutMs: 25_000,
        healthWorkflow: "account_access_runtime",
        healthExpectedErrorCodes: ["auth/too-many-requests", "auth/invalid-credential", "auth/invalid-input"],
        healthMinDegradedCount: 1,
        healthCriticalCount: 1,
        healthNetworkMinDegradedCount: 1,
        healthNetworkCriticalCount: 1,
      },
      addMemberRole: { route: "addMemberRole", requiresAuth: true },
      deleteMember: {
        route: "deleteMember",
        requiresAuth: true,
        directBaseUrl: AUTH_HANDLER_DIRECT_BASE_URL,
      },
      sendCustomLoginLink: { route: "sendCustomLoginLink", requiresAuth: true },
      sendLoginLink: { route: "sendLoginLink", requiresAuth: true },
      sendVerificationCode: {
        route: "sendVerificationCode",
        requiresAuth: false,
        ...ACCOUNT_ACCESS_RUNTIME_HEALTH,
      },
      validateLoginLink: { route: "validateLoginLink", requiresAuth: false, ...ACCOUNT_ACCESS_RUNTIME_HEALTH },
      startLoginLinkRecovery: {
        route: "startLoginLinkRecovery",
        requiresAuth: false,
        ...ACCOUNT_ACCESS_RUNTIME_HEALTH,
      },
      validateLoginLinkRecoveryCode: {
        route: "validateLoginLinkRecoveryCode",
        requiresAuth: false,
        ...ACCOUNT_ACCESS_RUNTIME_HEALTH,
      },
      validateVerificationCode: {
        route: "validateVerificationCode",
        requiresAuth: false,
        ...ACCOUNT_ACCESS_RUNTIME_HEALTH,
      },
      sendPublicBookingVerificationCode: {
        route: "sendPublicBookingVerificationCode",
        requiresAuth: true,
        healthWorkflow: "pre_onboarding_v2_runtime",
        healthExpectedErrorCodes: ["rate_limited", "resend_cooldown"],
        healthMinDegradedCount: 1,
        healthCriticalCount: 1,
        healthNetworkMinDegradedCount: 1,
        healthNetworkCriticalCount: 1,
      },
      checkPublicBookingContact: {
        route: "checkPublicBookingContact",
        requiresAuth: true,
        healthWorkflow: "pre_onboarding_v2_runtime",
        healthMinDegradedCount: 1,
        healthCriticalCount: 1,
        healthNetworkMinDegradedCount: 1,
        healthNetworkCriticalCount: 1,
      },
      validatePublicBookingVerificationCode: {
        route: "validatePublicBookingVerificationCode",
        requiresAuth: true,
        healthWorkflow: "pre_onboarding_v2_runtime",
        healthExpectedErrorCodes: ["rate_limited", "too_many_attempts"],
        healthMinDegradedCount: 1,
        healthCriticalCount: 1,
        healthNetworkMinDegradedCount: 1,
        healthNetworkCriticalCount: 1,
      },
    },
  },

  "facebook-page-handler": {
    localPrefix: "/facebook-page",
    endpoints: {
      createReviewPost: { route: "facebook/review-post", requiresAuth: false },
    },
  },

  "visit-review-handler": {
    localPrefix: "/visit-review",
    endpoints: {
      submitVisitReview: { route: "submitVisitReview", requiresAuth: true },
      scoreReviewSentiment: {
        route: "scoreReviewSentiment",
        requiresAuth: true,
      },
      getPublicReviewDetails: {
        route: "public/review-details",
        requiresAuth: false,
        includeAuthToken: false,
      },
    },
  },

  "referral-handler": {
    localPrefix: "/referral",
    endpoints: {
      setReferralCode: { route: "setReferralCode", requiresAuth: true },
      ensureReferralCode: { route: "ensureReferralCode", requiresAuth: true },
      getReferralItemsPage: {
        route: "getReferralItemsPage",
        requiresAuth: true,
      },
      syncReferralBucketCounts: {
        route: "syncReferralBucketCounts",
        requiresAuth: true,
      },
      applyReferralFromOwner: {
        route: "applyReferralFromOwner",
        requiresAuth: true,
      },
      ingestReferralEvent: { route: "ingestReferralEvent", requiresAuth: true },
      removeReferralCode: { route: "removeReferralCode", requiresAuth: true },
      applyReferralCode: { route: "applyReferralCode", requiresAuth: true },
    },
  },

  "member-state-handler": {
    localPrefix: "/member-state",
    endpoints: {
      resolveLocalPricingMarket: {
        route: "public/localPricing/resolve",
        requiresAuth: false,
        includeAuthToken: false,
        healthWorkflow: "local_market_pricing_resolution",
      },
      resolvePublicLocation: {
        route: "public/location/resolve",
        requiresAuth: false,
        includeAuthToken: false,
        healthWorkflow: "google_geo_pre_onboarding_location_resolution",
      },
      processDueLocalPricingWork: {
        route: "internal/localPricing/processDueWork",
        requiresAuth: true,
      },
      saveMemberAddress: {
        route: "saveMemberAddress",
        requiresAuth: true,
      },
      resolveMemberAddress: {
        route: "resolveMemberAddress",
        requiresAuth: true,
      },
      saveHelperAreaState: {
        route: "saveHelperAreaState",
        requiresAuth: true,
      },
      queueHelperCoverageRefresh: {
        route: "queueHelperCoverageRefresh",
        requiresAuth: true,
      },
      syncMemberServiceState: {
        route: "syncMemberServiceState",
        requiresAuth: true,
      },
      recordDashboardServiceView: {
        route: "recordDashboardServiceView",
        requiresAuth: true,
      },
      grantComplimentaryLifetimeMembership: {
        route: "grantComplimentaryLifetimeMembership",
        requiresAuth: true,
      },
      setMemberDormantState: {
        route: "setMemberDormantState",
        requiresAuth: true,
      },
      syncHelperAreaProjection: {
        route: "syncHelperAreaProjection",
        requiresAuth: true,
      },
    },
  },

  "maintenance-handler": {
    localPrefix: "/maintenance",
    endpoints: {
      updateAnalytics: {
        route: "updateAnalytics",
        requiresAuth: false,
      },
      runDailyHelperMaintenance: {
        route: "runDailyHelperMaintenance",
        requiresAuth: true,
      },
      runHelperAnalyticsMaintenance: {
        route: "runHelperAnalyticsMaintenance",
        requiresAuth: true,
      },
      recordReportingActivity: {
        route: "recordReportingActivity",
        requiresAuth: true,
      },
      getCriticalHealthAlertReview: {
        route: "getCriticalHealthAlertReview",
        requiresAuth: true,
      },
      runCriticalFlowHealthReview: {
        route: "runCriticalFlowHealthReview",
        requiresAuth: true,
      },
      applyWelcomeDiscountSuppression: {
        route: "applyWelcomeDiscountSuppression",
        requiresAuth: true,
      },
    },
  },
};

const DEFAULT_RETRYABLE_STATUSES = [429, 502, 503, 504];

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const parseRetryAfterMs = (value) => {
  if (!value) return null;

  const numericSeconds = Number(value);
  if (Number.isFinite(numericSeconds) && numericSeconds >= 0) {
    return numericSeconds * 1000;
  }

  const retryAt = new Date(value).getTime();
  if (Number.isFinite(retryAt)) {
    return Math.max(0, retryAt - Date.now());
  }

  return null;
};

const parseResponsePayload = async (res) => {
  const text = await res.text().catch(() => "");
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch (error) {
    return text;
  }
};

const getPayloadErrorMessage = (payload) => {
  if (!payload) return "";
  if (typeof payload === "string") return payload.trim();
  if (typeof payload?.message === "string") return payload.message.trim();
  if (typeof payload?.error === "string") return payload.error.trim();
  if (typeof payload?.error?.message === "string") {
    return payload.error.message.trim();
  }

  return "";
};

const getPayloadErrorCode = (payload) => {
  if (!payload || typeof payload !== "object") return "";
  if (typeof payload.code === "string") return payload.code.trim();
  if (typeof payload.error?.code === "string") {
    return payload.error.code.trim();
  }
  if (typeof payload.error === "string") return payload.error.trim();
  return "";
};

const getApiDependencyHealthReportUrl = () =>
  isLocalhost
    ? `${LOCAL_API_BASE_URL}/api/crash/report`
    : "/client-errors/report";

const shouldReportApiDependencyFailure = ({
  status,
  errorCode = "",
  expectedErrorCodes = [],
}) => {
  if (status == null || status === "") return true;
  const numericStatus = Number(status);
  if (
    numericStatus >= 400 &&
    numericStatus < 500 &&
    expectedErrorCodes.includes(String(errorCode || "").trim())
  ) {
    return false;
  }
  return (
    numericStatus === 429 ||
    numericStatus >= 500 ||
    !Number.isFinite(numericStatus)
  );
};

const sendApiDependencyHealthReport = (payload) => {
  if (typeof window === "undefined") return;

  const reportBody = JSON.stringify(payload);
  const reportUrl = getApiDependencyHealthReportUrl();

  if (
    typeof navigator !== "undefined" &&
    typeof navigator.sendBeacon === "function" &&
    typeof Blob === "function"
  ) {
    const sent = navigator.sendBeacon(
      reportUrl,
      new Blob([reportBody], { type: "application/json" }),
    );
    if (sent) return;
  }

  if (typeof fetch !== "function") return;
  fetch(reportUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: reportBody,
    keepalive: true,
  }).catch(() => {});
};

const queueApiDependencyFailureReport = ({
  serviceName,
  functionName,
  endpoint,
  route,
  status,
  message,
  requestId,
  errorType = "http_response",
  errorCode = "",
}) => {
  if (
    !shouldReportApiDependencyFailure({
      status,
      errorCode,
      expectedErrorCodes: endpoint.healthExpectedErrorCodes,
    })
  ) {
    return;
  }

  const hasHttpStatus =
    status != null && status !== "" && Number.isFinite(Number(status));

  sendApiDependencyHealthReport({
    schemaVersion: "api_dependency_health/client/v1",
    eventName: "api_dependency_failure",
    service: serviceName,
    provider: "cloud_run",
    operation: functionName,
    workflow:
      endpoint.healthWorkflow || `client_api:${serviceName}:${functionName}`,
    route,
    httpStatus: hasHttpStatus ? Number(status) : null,
    statusClass: hasHttpStatus
      ? `${Math.floor(Number(status) / 100)}xx`
      : errorType === "response_contract" ? "contract_failure" : "network_error",
    errorCode:
      errorCode || (hasHttpStatus ? `http_${status}` : "network_error"),
    errorType,
    message,
    requestId,
    pathname: window.location?.pathname || "",
    windowMinutes: endpoint.healthWindowMinutes || 15,
    minDegradedCount: hasHttpStatus
      ? endpoint.healthMinDegradedCount || 3
      : (endpoint.healthNetworkMinDegradedCount ??
        endpoint.healthMinDegradedCount ??
        3),
    criticalCount: hasHttpStatus
      ? endpoint.healthCriticalCount || 8
      : (endpoint.healthNetworkCriticalCount ??
        endpoint.healthCriticalCount ??
        8),
  });
};

// Callers validate their own successful response contract; never send response data.
export const reportCloudRunResponseContractFailure = (serviceName, functionName) => {
  const endpoint = serviceEndpointMap[serviceName]?.endpoints?.[functionName];
  if (!endpoint) return;
  try {
    queueApiDependencyFailureReport({
      serviceName, functionName, endpoint, route: endpoint.route,
      status: null, errorCode: "response_contract_failure", errorType: "response_contract",
      message: "The service returned an invalid response.",
    });
  } catch (_error) {
    // Diagnostics must never replace the original failure.
  }
};

let cloudRunLabOverrides = {
  callCloudRunFunction: null,
};

export const setCloudRunLabOverrides = (overrides = {}) => {
  cloudRunLabOverrides = {
    ...cloudRunLabOverrides,
    ...overrides,
  };
};

export const callCloudRunFunction = async (
  serviceName,
  functionName,
  data = {},
  options = {},
) => {
  if (typeof cloudRunLabOverrides.callCloudRunFunction === "function") {
    return cloudRunLabOverrides.callCloudRunFunction(
      serviceName,
      functionName,
      data,
      options,
    );
  }

  const service = serviceEndpointMap[serviceName];
  if (!service) throw new Error(`Unknown service: ${serviceName}`);

  const endpoint = service.endpoints?.[functionName];
  if (!endpoint) {
    throw new Error(
      `Unknown function '${functionName}' for service '${serviceName}'`,
    );
  }

  const route = endpoint.route;
  const requestId = createApiRequestId();

  const fetchOptions = options.fetchOptions || {};
  const requestTimeoutMs = Math.max(
    0,
    Number(options.requestTimeoutMs ?? endpoint.requestTimeoutMs) || 0,
  );
  const retry = options.retry ?? endpoint.retry ?? null;
  const maxRetries = Math.max(0, retry?.retries || 0);
  const retryOnStatuses = retry?.retryOnStatuses || DEFAULT_RETRYABLE_STATUSES;
  const backoffMs = Math.max(0, retry?.backoffMs || 700);
  const reportRetryFailureImmediately =
    options.reportRetryFailureImmediately ??
    endpoint.reportRetryFailureImmediately ??
    (endpoint.healthCriticalCount === 1);
  const reportDependencyHealth = options.reportDependencyHealth !== false;
  let failureReported = false;

  const reportFailureOnce = (failure) => {
    if (!reportDependencyHealth || failureReported) return;
    queueApiDependencyFailureReport({
      serviceName,
      functionName,
      endpoint: options.healthWorkflow
        ? { ...endpoint, healthWorkflow: options.healthWorkflow,
            healthNetworkMinDegradedCount: options.healthNetworkMinDegradedCount,
            healthNetworkCriticalCount: options.healthNetworkCriticalCount }
        : endpoint,
      route,
      ...failure,
    });
    failureReported = true;
  };
  const emitTransportEvent = (phase, properties = {}) => {
    if (typeof options.onTransportEvent !== "function") return;
    try {
      Promise.resolve(options.onTransportEvent({ phase, requestId, ...properties })).catch(
        () => {},
      );
    } catch (_error) {
      // Diagnostics must never interrupt the request.
    }
  };

  const auth = getAuthClient();
  const user = auth.currentUser;

  const requiresAuth = options.requiresAuth ?? endpoint.requiresAuth ?? true;
  const includeAuthToken =
    options.includeAuthToken ?? endpoint.includeAuthToken ?? true;

  const useLocalDevAdmin =
    isLocalhost && endpoint.localDevAdmin === true && !user;

  if (requiresAuth && !user && !useLocalDevAdmin) {
    throw new Error("User not authenticated");
  }

  const shouldAttachAuth = includeAuthToken && Boolean(user);
  const includeAppCheckToken =
    options.includeAppCheckToken ?? endpoint.includeAppCheckToken ?? false;
  const credentialStartedAt = Date.now();
  let credentialPhase = "credential_preparation";
  let firebaseIdTokenLatencyMs = 0;
  let firebaseAppCheckLatencyMs = 0;
  let idToken = null;
  let appCheckResult = { token: null };
  let localDevAuthHeaders = {};
  try {
    if (shouldAttachAuth) {
      credentialPhase = "firebase_id_token";
      const phaseStartedAt = Date.now();
      idToken = await user.getIdToken();
      firebaseIdTokenLatencyMs = Math.max(0, Date.now() - phaseStartedAt);
    }
    if (includeAppCheckToken) {
      credentialPhase = "firebase_app_check";
      const phaseStartedAt = Date.now();
      appCheckResult = await getFirebaseAppCheckToken();
      firebaseAppCheckLatencyMs = Math.max(0, Date.now() - phaseStartedAt);
    }
    credentialPhase = "local_auth_headers";
    localDevAuthHeaders = shouldAttachAuth
      ? await getLocalDevAuthHeaders(user)
      : useLocalDevAdmin
        ? { "x-dev-uid": "local-geo-preview", "x-dev-role": "admin" }
        : {};
  } catch (error) {
    emitTransportEvent("request_failed", {
      transportPhase: credentialPhase,
      errorCode: error?.code || "credential_preparation_failed",
      latencyMs: Math.max(0, Date.now() - credentialStartedAt),
      credentialLatencyMs: Math.max(0, Date.now() - credentialStartedAt),
      firebaseIdTokenLatencyMs,
      firebaseAppCheckLatencyMs,
    });
    if (error && typeof error === "object") {
      error.endpoint = `${serviceName}/${functionName}`;
      error.httpStatus = null;
      error.transportPhase = credentialPhase;
      error.requestId = requestId;
      error.credentialLatencyMs = Math.max(0, Date.now() - credentialStartedAt);
    }
    reportFailureOnce({
      status: null,
      message: error?.message || "Credential preparation failed",
      requestId,
      errorCode: error?.code || "credential_preparation_failed",
      errorType: credentialPhase,
    });
    throw error;
  }

  const apiPath = `/api${service.localPrefix || ""}/${route}`;
  const url = resolveCloudRunEndpointUrl({
    local: isLocalhost,
    localBaseUrl: LOCAL_API_BASE_URL,
    apiPath,
    directBaseUrl: endpoint.directBaseUrl,
    route,
  });

  for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
    let res = null;
    let payload = null;
    let requestPhase = "request_dispatch";
    const requestStartedAt = Date.now();
    const credentialLatencyMs = Math.max(
      0,
      requestStartedAt - credentialStartedAt,
    );
    let responseReceivedAt = null;
    try {
      const createRequest = async ({ signal } = {}) => {
        if (fetchOptions.signal?.aborted) {
          throw Object.assign(new Error("Request cancelled before sending."), { name: "AbortError" });
        }
        requestPhase = "response_headers";
        const response = await fetch(url, {
          method: "POST",
          ...fetchOptions,
          ...(signal ? { signal } : {}),
          headers: buildApiRequestHeaders({
            "Content-Type": "application/json",
            ...localDevAuthHeaders,
            ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
            ...(idToken ? { "x-ll-firebase-auth": `Bearer ${idToken}` } : {}),
            ...(appCheckResult.token
              ? { "X-Firebase-AppCheck": appCheckResult.token }
              : {}),
            ...normalizeRequestHeaders(fetchOptions.headers),
          }, isLocalhost || !endpoint.directBaseUrl ? requestId : null),
          body: JSON.stringify(data),
        });
        responseReceivedAt = Date.now();
        requestPhase = "response_body";
        const parsedPayload = await parseResponsePayload(response);
        return { response, payload: parsedPayload };
      };
      const requestResult = requestTimeoutMs
        ? await runWithClientDeadline({
            createRequest,
            timeoutMs: requestTimeoutMs,
            timeoutMessage: `${functionName} timed out.`,
          })
        : await createRequest();
      res = requestResult.response;
      payload = requestResult.payload;
    } catch (error) {
      const canRetry = attempt < maxRetries;
      const timedOut = error?.code === "client-request-timeout";
      emitTransportEvent("request_failed", {
        attemptCount: attempt + 1,
        errorCode: error?.code || "network_error",
        transportPhase: requestPhase,
        latencyMs: Math.max(0, Date.now() - requestStartedAt),
        credentialLatencyMs,
        firebaseIdTokenLatencyMs,
        firebaseAppCheckLatencyMs,
        requestLatencyMs: Math.max(0, Date.now() - requestStartedAt),
        responseBodyLatencyMs: responseReceivedAt
          ? Math.max(0, Date.now() - responseReceivedAt)
          : 0,
      });
      if (!canRetry || reportRetryFailureImmediately) {
        reportFailureOnce({
          status: null,
          message: error?.message || "Network request failed",
          requestId,
          errorCode: timedOut ? "client_request_timeout" : "network_error",
          errorType: timedOut
            ? `client_timeout_${requestPhase}`
            : "network_error",
        });
      }
      if (error && typeof error === "object") {
        error.endpoint = `${serviceName}/${functionName}`;
        error.httpStatus = null;
        error.transportPhase = requestPhase;
        error.requestId = requestId;
        error.requestLatencyMs = Math.max(0, Date.now() - requestStartedAt);
        error.attemptCount = attempt + 1;
      }
      if (!canRetry) throw error;
      await sleep(backoffMs * (attempt + 1));
      continue;
    }

    if (res.ok) {
      emitTransportEvent("request_succeeded", {
        attemptCount: attempt + 1,
        transportPhase: "complete",
        httpStatus: res.status,
        latencyMs: Math.max(0, Date.now() - credentialStartedAt),
        credentialLatencyMs,
        firebaseIdTokenLatencyMs,
        firebaseAppCheckLatencyMs,
        requestLatencyMs: Math.max(
          0,
          (responseReceivedAt || Date.now()) - requestStartedAt,
        ),
        responseBodyLatencyMs: responseReceivedAt
          ? Math.max(0, Date.now() - responseReceivedAt)
          : 0,
      });
      return payload;
    }

    const message =
      getPayloadErrorMessage(payload) ||
      `Request failed with status ${res.status}`;

    const err = new Error(message);
    const responseErrorCode = getPayloadErrorCode(payload);
    if (responseErrorCode) err.code = responseErrorCode;
    err.httpStatus = res.status;
    err.data = payload;
    err.endpoint = `${serviceName}/${functionName}`;
    err.requestId = requestId;
    err.transportPhase = "http_response";
    err.requestLatencyMs = Math.max(0, Date.now() - requestStartedAt);
    err.attemptCount = attempt + 1;

    emitTransportEvent("request_failed", {
      attemptCount: attempt + 1,
      errorCode: responseErrorCode || `http_${res.status}`,
      transportPhase: "http_response",
      httpStatus: res.status,
      latencyMs: Math.max(0, Date.now() - requestStartedAt),
      credentialLatencyMs,
      firebaseIdTokenLatencyMs,
      firebaseAppCheckLatencyMs,
      requestLatencyMs: Math.max(
        0,
        (responseReceivedAt || Date.now()) - requestStartedAt,
      ),
      responseBodyLatencyMs: responseReceivedAt
        ? Math.max(0, Date.now() - responseReceivedAt)
        : 0,
    });

    const canRetry =
      attempt < maxRetries && retryOnStatuses.includes(res.status);

    if (!canRetry || reportRetryFailureImmediately) {
      reportFailureOnce({
        status: res.status,
        message,
        requestId: err.requestId,
        errorCode: responseErrorCode,
      });
    }

    if (!canRetry) {
      throw err;
    }

    const retryAfterMs = parseRetryAfterMs(res.headers.get("retry-after"));
    const delayMs =
      retryAfterMs !== null ? retryAfterMs : backoffMs * (attempt + 1);
    await sleep(delayMs);
  }
};

export default callCloudRunFunction;
